import { useHistory, useParams } from 'react-router-dom';

import { updateProfile } from 'flow/actions/profile-actions';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { SettingsType } from 'flow/types/CallRecording';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    getCallRecordingTitle,
    getCallRecordingValue,
} from 'flow/utils/call-recording-monitoring-utils';
import { getProfileDTOWithValue } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';

const ProfileCallRecording = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { getProfile, hasPermissionToProfile } = useProfiles(extensionKey);
    const profile = getProfile(profileKey);

    if (!profile) {
        return null;
    }

    const handleToggleCoListening = (value: boolean) =>
        dispatch(
            updateProfile(
                extensionKey,
                getProfileDTOWithValue(profile, ['canBeCoMonitored'], value),
            ),
        );

    return (
        <Page
            header={getCallRecordingTitle(profile)}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <>
                <RouteLink
                    to={generatePath(
                        routes[
                            'settings.profiles.profileKey.call-recording-and-monitoring.settingsType'
                        ],
                        {
                            extensionKey,
                            profileKey,
                            settingsType: SettingsType.Business,
                        },
                    )}
                    description={i18next.t(
                        `settings.call_recording.${getCallRecordingValue(profile, SettingsType.Business, true)}`,
                    )}
                >
                    {i18next.t('settings.call_recording')}
                </RouteLink>

                {hasPermissionToProfile('personalRecordCalls') ||
                hasPermissionToProfile('personalAutoTranscribe') ? (
                    <RouteLink
                        to={generatePath(
                            routes[
                                'settings.profiles.profileKey.call-recording-and-monitoring.settingsType'
                            ],
                            {
                                extensionKey,
                                profileKey,
                                settingsType: SettingsType.Personal,
                            },
                        )}
                        description={i18next.t(
                            `settings.call_recording.${getCallRecordingValue(profile, SettingsType.Personal, true)}`,
                        )}
                    >
                        {i18next.t('settings.call_recording_personal')}
                    </RouteLink>
                ) : null}

                <SettingRow
                    cta={
                        <Toggle
                            value={profile.get('canBeCoMonitored', false)}
                            disabled={
                                !hasPermissionToProfile('canBeCoMonitored')
                            }
                            onChange={handleToggleCoListening}
                            text={i18next.t('settings.call_monitoring')}
                        />
                    }
                    disabled={!hasPermissionToProfile('canBeCoMonitored')}
                    information={
                        !hasPermissionToProfile('canBeCoMonitored') &&
                        i18next.t('settings.call_monitoring.no_feature')
                    }
                >
                    {i18next.t('settings.call_monitoring')}
                </SettingRow>
            </>
        </Page>
    );
};

export default ProfileCallRecording;
